import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Search, ClipboardList } from 'lucide-react';
import { api } from '../../lib/api';
import { Pagination } from '../../components/admin/Pagination';
import { SegmentedTabs } from '../../components/admin/SegmentedTabs';
import { SortableTh, TableSkeleton } from '../../components/admin/SortableTable';
import { TableEmpty } from '../../components/admin/TableEmpty';
import { useDebounce } from '../../hooks/useDebounce';
import { useCurrency } from '../../context/CurrencyContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

const STATUSES = ['pending', 'confirmed', 'shipped', 'delivered', 'cancelled'];
const STATUS_COLORS = {
    pending: '#B7791F', confirmed: '#1F7A5A', shipped: '#5A2432', delivered: '#1A1416', cancelled: '#B42318',
};
const TAB_OPTIONS = [{ key: 'all', label: 'All' }, ...STATUSES.map((s) => ({ key: s, label: s }))];

export default function AdminRequests() {
    const [items, setItems] = useState([]);
    const [tab, setTab] = useState('all');
    const [selected, setSelected] = useState(null);
    const [saving, setSaving] = useState(false);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [sort, setSort] = useState({ by: 'created_at', dir: 'desc' });
    const [loading, setLoading] = useState(true);
    const PAGE_SIZE = 10;
    const debouncedSearch = useDebounce(search);
    const { format } = useCurrency();

    const load = async () => {
        setLoading(true);
        try {
            const params = { page, page_size: PAGE_SIZE, search: debouncedSearch || undefined, sort_by: sort.by, sort_dir: sort.dir };
            if (tab !== 'all') params.status = tab;
            const r = await api.get('/admin/purchase-requests', { params });
            setItems(r.data.items);
            setTotal(r.data.total);
        } finally { setLoading(false); }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [tab, page, debouncedSearch, sort]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { setPage(1); }, [tab, debouncedSearch, sort]);

    const updateStatus = async (status) => {
        if (!selected) return;
        setSaving(true);
        try {
            const r = await api.put(`/admin/purchase-requests/${selected.id}`, { status });
            setSelected(r.data); await load();
            toast.success('Status updated.');
        } catch { toast.error('Update failed'); } finally { setSaving(false); }
    };

    const saveNotes = async (notes) => {
        if (!selected) return;
        try { const r = await api.put(`/admin/purchase-requests/${selected.id}`, { admin_notes: notes }); setSelected(r.data); await load(); toast.success('Notes saved.'); }
        catch { toast.error('Save failed'); }
    };

    return (
        <div data-testid="admin-requests-page">
            <div className="mb-8">
                <p className="eyebrow" style={{ color: 'var(--brand-burgundy)' }}>Atelier</p>
                <h1 className="font-serif text-3xl sm:text-4xl mt-1" style={{ color: 'var(--brand-burgundy)' }}>Purchase Requests</h1>
            </div>

            <div className="mb-6">
                <SegmentedTabs value={tab} onChange={setTab} options={TAB_OPTIONS} testIdPrefix="admin-requests-tab" />
            </div>

            <div className="mb-4 flex items-center justify-between gap-4">
                <div className="relative max-w-sm w-full">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--ink-muted)' }} />
                    <input
                        data-testid="admin-requests-search-input"
                        type="text"
                        placeholder="Search by reference, name, email, location..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="input-luxury pl-9"
                    />
                </div>
                <p className="text-xs whitespace-nowrap hidden sm:block" style={{ color: 'var(--ink-muted)' }} data-testid="admin-requests-count">{total} result{total === 1 ? '' : 's'}</p>
            </div>

            <div className="luxury-card overflow-hidden">
                <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[720px]" data-testid="admin-requests-table">
                    <thead className="bg-[var(--brand-rose)]/40">
                        <tr className="text-left">
                            <SortableTh label="Reference" field="reference" sort={sort} onSort={setSort} />
                            <SortableTh label="Customer" field="customer_name" sort={sort} onSort={setSort} />
                            <th className="p-4">Location</th>
                            <th className="p-4">Items</th>
                            <SortableTh label="Total" field="subtotal" sort={sort} onSort={setSort} />
                            <SortableTh label="Status" field="status" sort={sort} onSort={setSort} />
                            <SortableTh label="Date" field="created_at" sort={sort} onSort={setSort} />
                        </tr>
                    </thead>
                    {loading ? <TableSkeleton cols={7} /> : (
                    <tbody>
                        {items.map((r) => (
                            <tr key={r.id} onClick={() => setSelected(r)} className="border-t border-[rgba(90,36,50,0.08)] cursor-pointer hover:bg-[var(--brand-rose)]/20 transition-colors" data-testid={`admin-request-row-${r.reference}`}>
                                <td className="p-4 font-mono text-xs">{r.reference}</td>
                                <td className="p-4"><div className="font-serif text-base" style={{ color: 'var(--brand-burgundy)' }}>{r.customer_name}</div><div className="text-xs" style={{ color: 'var(--ink-muted)' }}>{r.customer_email}</div></td>
                                <td className="p-4 text-xs">{r.city}, {r.country}</td>
                                <td className="p-4">{r.items?.length || 0}</td>
                                <td className="p-4">{format(r.subtotal || 0)}</td>
                                <td className="p-4"><span className="text-xs px-2.5 py-1 rounded-full text-white capitalize" style={{ backgroundColor: STATUS_COLORS[r.status] || '#5A2432' }}>{r.status}</span></td>
                                <td className="p-4 text-xs" style={{ color: 'var(--ink-muted)' }}>{new Date(r.created_at).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}</td>
                            </tr>
                        ))}
                    </tbody>
                    )}
                </table>
                </div>
                {!loading && items.length === 0 && (
                    <TableEmpty icon={ClipboardList} title="No requests" subtitle="Purchase requests will appear here as customers check out." testId="admin-requests-empty" />
                )}
            </div>

            <Pagination page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} testIdPrefix="admin-requests-pagination" />

            <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white" data-testid="admin-request-detail-dialog">
                    {selected && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="font-serif text-2xl" style={{ color: 'var(--brand-burgundy)' }}>{selected.reference}</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4">
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div><p className="eyebrow" style={{ color: 'var(--ink-muted)' }}>Customer</p><p>{selected.customer_name}</p><p>{selected.customer_email}</p><p>{selected.customer_phone}</p></div>
                                    <div><p className="eyebrow" style={{ color: 'var(--ink-muted)' }}>Shipping</p><p>{selected.address}</p><p>{selected.city}, {selected.country} {selected.postal_code}</p></div>
                                </div>
                                {selected.notes && <div><p className="eyebrow" style={{ color: 'var(--ink-muted)' }}>Customer Notes</p><p className="text-sm italic">"{selected.notes}"</p></div>}
                                <div>
                                    <p className="eyebrow mb-2" style={{ color: 'var(--ink-muted)' }}>Items</p>
                                    <ul className="divide-y divide-[rgba(90,36,50,0.08)] border border-[rgba(90,36,50,0.08)] rounded-lg">
                                        {selected.items?.map((it, i) => (
                                            <li key={i} className="p-3 flex items-center gap-3">
                                                {it.image && <img src={it.image} alt={it.name} className="w-12 h-14 object-cover rounded" />}
                                                <div className="flex-1"><p className="font-serif text-base" style={{ color: 'var(--brand-burgundy)' }}>{it.name}</p><p className="text-xs" style={{ color: 'var(--ink-muted)' }}>{it.size} · {it.color} · Qty {it.quantity}</p>{it.special_request && <p className="text-xs mt-0.5 italic" style={{ color: 'var(--brand-burgundy)' }}>Note: {it.special_request}</p>}</div>
                                                <p className="text-sm">{format(it.price * it.quantity)}</p>
                                            </li>
                                        ))}
                                    </ul>
                                    <div className="mt-3 text-right font-serif text-xl" style={{ color: 'var(--brand-burgundy)' }}>Total: {format(selected.subtotal || 0)}</div>
                                </div>
                                <div>
                                    <p className="eyebrow mb-1" style={{ color: 'var(--ink-muted)' }}>Status</p>
                                    <Select value={selected.status} onValueChange={updateStatus}>
                                        <SelectTrigger data-testid="admin-request-status-select" className="bg-white"><SelectValue /></SelectTrigger>
                                        <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <p className="eyebrow mb-1" style={{ color: 'var(--ink-muted)' }}>Admin Notes</p>
                                    <textarea rows={3} className="input-luxury resize-none" defaultValue={selected.admin_notes} onBlur={(e) => saveNotes(e.target.value)} data-testid="admin-request-notes" />
                                </div>
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
